import { DateTime } from 'luxon';
import { create } from 'zustand';

import type { ServerError } from 'models';
import type { DollarsString } from 'models/dollars';
import { parseDollarsString } from 'models/dollars';
import type { OrderStatus, OrderVo } from 'models/order';
import type { UserOrderEntry } from 'models/userOrder';
import {
    isUserOrderPending,
    selectRestingUserOrdersInMarket,
    userOrderEntryFromRestVo,
    userOrderEntryFromWsDto,
} from 'models/userOrder';
import type { UserOrderDTO, WsMessage, WsResponse, WsUnsubscribeCallback } from 'models/ws';
import { useBannerStore } from 'stores/zustand/bannerStore';
import { resolveEventTickerForMarketId } from 'stores/zustand/marketEntitiesStore';
import { getSubaccountNumber, useSubAccountStore } from 'stores/zustand/subAccountStore';
import { tradingContextSelectors, useTradingContextStore } from 'stores/zustand/tradingContextStore';
import { getOrders } from 'transport/domains/orders';
import { legacyExchangeWsClient } from 'transport/wsClients';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { noop } from 'utils/noop';

import { useAuthStore } from './authStore';

export interface LocalAmendMarker {
    /** Server update timestamp the locally confirmed amend was based on. */
    basedOnLastUpdateTs: string;
    /** `Date.now()` when the confirmed amend was written into the cache. */
    confirmedAtMillis: number;
    /** Price the confirmation set — a revision that predates it still carries the old one. */
    priceDollars: DollarsString;
    /** Total order size the confirmation set (fills + remaining), likewise pre-amend on older revisions. */
    initialCount: number;
}

/**
 * Live cache of the user's orders, keyed by order id. Hydrated by the
 * event/market-scoped REST fetches (`refreshOrdersForEvent` below, the
 * resting-orders modal, the portfolio all-resting-orders load), kept fresh by
 * the `user_orders` WS channel (via this module's own buffered drain, which
 * owns trading-context gating), and trimmed by cancel confirmations and
 * reconciliation refetches.
 *
 * Order history one-shots (order history modal, activity CSV export) do NOT
 * write here — they are plain React Query fetches. Only order data that must
 * update live belongs in this store.
 */
export interface UserOrdersState {
    orders: ReadonlyMap<string, UserOrderEntry>;
    /**
     * Successful V2 create results awaiting their first canonical REST/WS
     * row. Explicit ids are used instead of inferring from order fields: the
     * placeholder carries a real market ticker so it is immediately
     * cancelable.
     */
    locallySubmittedOrderIds: ReadonlySet<string>;
    /**
     * Locally confirmed amends, keyed by order id. Until the server publishes
     * a revision that reflects the amend, REST/WS rows can still carry the
     * pre-amend values and must not replace the local success result — see
     * `shouldIgnoreIncomingOrder`. `confirmedAtMillis` bounds both that
     * suppression and the reconciliation one
     * (`dropRestingOrdersMissingFromRefetch`), so nothing is protected forever.
     */
    locallyAmendedOrders: ReadonlyMap<string, LocalAmendMarker>;
    /**
     * True once the portfolio's load-everything flow has fetched every resting
     * order AND loaded the backing markets/events for display. Reset alongside
     * `orders` so a subaccount switch or logout re-triggers the full load.
     */
    loadedAllRestingOrders: boolean;
    actions: UserOrdersActions;
}

/** A `user_orders` message paired with when the client RECEIVED it. */
export interface ReceivedWsUserOrder {
    dto: UserOrderDTO;
    /**
     * `Date.now()` at enqueue, not at apply. Whether a row is stale is a
     * property of when it arrived, and the drain that applies it can run
     * arbitrarily later than that — `setTimeout` is throttled in a backgrounded
     * tab and suspended outright in a frozen one — so amend protection is
     * evaluated against this rather than the clock at drain time.
     */
    receivedAtMillis: number;
}

export interface UserOrdersActions {
    /**
     * Make a successful V2 create result visible immediately while the
     * canonical REST/WS row is still in flight. The create response is only a
     * partial order snapshot, so an already-cached server row always wins.
     */
    applySubmittedOrder: (vo: OrderVo) => void;
    /**
     * Upsert a batch of REST rows. Identity-preserves unchanged entries.
     *
     * `requestedAtMillis` is when the caller ISSUED the request these rows came
     * back from — never when the response landed. Order requests carry the
     * repository's long default timeout and can queue behind the rate limiter,
     * so a response can arrive minutes after the state it captured; amend
     * protection is judged against the request boundary for that reason.
     */
    hydrateFromRest: (vos: OrderVo[], requestedAtMillis: number) => void;
    /** Apply a drained batch of `user_orders` WS messages (already gated/deduped by the caller). */
    applyWsOrders: (rows: readonly ReceivedWsUserOrder[]) => void;
    /** Drop a single order (confirmed cancel). */
    removeOrder: (orderId: string) => void;
    /** Drop confirmed canceled orders with one Map clone and one store update. */
    removeOrders: (orderIds: readonly string[]) => void;
    /** Apply the lightweight V2 amend result without fabricating a REST OrderVo. */
    applyAmendResult: (
        orderId: string,
        expectedLastUpdateTs: string,
        result: {
            priceDollars: DollarsString;
            fillCount: number;
            remainingCount: number;
            initialCount: number;
            status: OrderStatus;
        },
    ) => void;
    /**
     * Applied after a reconciliation refetch pages through an event's CURRENT
     * resting set: an order that was cached as resting when the refetch
     * started (`snapshot`), came back in none of the pages, and hasn't been
     * touched since the snapshot must have left the resting set
     * (filled/canceled) while unwatched. Its final status isn't knowable from
     * a resting-only response, so the stale row is dropped rather than given
     * a fabricated status — matching how a confirmed cancel removes its row.
     * An order whose `lastUpdateTs` moved since the snapshot received fresher
     * data (a WS message, or another REST write) while the refetch was in
     * flight; trust that instead. An order that turned `pending` since the
     * snapshot (a fresh optimistic row) isn't in the resting set a
     * resting-only refetch enumerates, so it's kept too.
     *
     * A locally confirmed amend keeps its last server timestamp until the
     * server publishes a newer revision, so the `lastUpdateTs` guard above
     * can't tell an amended row apart from an untouched one. `snapshotAtMillis`
     * (when the caller took `snapshot`) scopes that protection to the
     * reconciliations that can't see the amend yet: one that started before
     * the amend confirmed, or within `AMEND_PROTECTION_WINDOW_MS` after it.
     * Any later refetch is authoritative and may drop the row — otherwise an
     * amended order that filled or was canceled during a WS gap would never
     * appear in a `status=resting` response again and would sit in the cache
     * as a ghost until logout.
     */
    dropRestingOrdersMissingFromRefetch: (
        snapshot: ReadonlyMap<string, string>,
        fetchedOrderIds: ReadonlySet<string>,
        snapshotAtMillis: number,
    ) => void;
    allRestingOrdersLoaded: () => void;
    reset: () => void;
}

function userOrderEntriesEqual(a: UserOrderEntry, b: UserOrderEntry): boolean {
    return (
        a.id === b.id &&
        a.userId === b.userId &&
        a.marketId === b.marketId &&
        a.marketTicker === b.marketTicker &&
        a.exchangeShard === b.exchangeShard &&
        a.isYes === b.isYes &&
        a.isSell === b.isSell &&
        a.priceDollars === b.priceDollars &&
        a.createTs === b.createTs &&
        a.lastUpdateTs === b.lastUpdateTs &&
        a.expirationTs === b.expirationTs &&
        a.status === b.status &&
        a.orderAction === b.orderAction &&
        a.orderType === b.orderType &&
        a.fillCount === b.fillCount &&
        a.remainingCount === b.remainingCount &&
        a.initialCount === b.initialCount &&
        a.takerFillCost === b.takerFillCost &&
        a.makerFillCost === b.makerFillCost &&
        a.takerFillFees === b.takerFillFees &&
        a.makerFillFees === b.makerFillFees
    );
}

/**
 * Upper bound on how long the `user_orders` pump holds a message before
 * applying it. Its flush-timing heuristics only run when a message ARRIVES, so
 * `queueWsUserOrderMessages` schedules a drain to enforce this bound even when
 * the channel goes idle with rows still buffered.
 */
const WS_USER_ORDER_MAX_BUFFER_MS = 4 * 1000;

/**
 * How long after a confirmed amend an incoming row can still be carrying a
 * pre-amend view of the order. A row the WS pump was already holding when the
 * amend confirmed is settled right there
 * (`discardPreAmendBufferedWsUserOrder`), so this window covers what arrives
 * AFTER that boundary: a revision the server
 * generated before applying the amend but delivered later, and REST responses
 * — a reconciliation refetch's pages in particular — read from a path that lags
 * the write path the amend was confirmed on. Sized off the pump's own buffering
 * bound plus headroom for that delivery delay. Within the window such data
 * isn't authoritative about the amended row; past it, it is — which is what
 * keeps a missed fill/cancel from stranding the row (or its marker) in the
 * cache forever.
 *
 * It bounds DELIVERY delay, which the client can only measure while it is
 * actually able to receive — hence `deliveryResumedAtMillis`, which restarts
 * the window after a stall so a disconnected or frozen client can't age out of
 * protection while a pre-amend frame sits undelivered.
 */
const AMEND_PROTECTION_WINDOW_MS = WS_USER_ORDER_MAX_BUFFER_MS + 6 * 1000;

/**
 * `Date.now()` at the last moment message delivery could have RESUMED after
 * being stalled — the socket reconnecting, or the tab coming back to the
 * foreground after being throttled/frozen (a frozen page defers the `message`
 * events for frames the network already delivered, so even receipt time is
 * pushed arbitrarily far past when the server generated the row).
 *
 * The amend window is measured from the later of this and the confirmation, so
 * it bounds a window's worth of live delivery rather than wall-clock time the
 * client spent unable to receive anything. Without it, any stall longer than
 * the window would hand a pre-amend row the "past the window, therefore
 * authoritative" verdict and revert a confirmed amend. Set unconditionally on
 * (re)connect and on every foreground return: it only ever matters while an
 * amend marker is live, which is a few seconds at a time.
 */
let deliveryResumedAtMillis = 0;

/**
 * Start of the protection window for one confirmed amend: the confirmation
 * itself, or a later resumption of delivery — whichever is more recent.
 */
function amendProtectionStartMillis(amend: LocalAmendMarker): number {
    return Math.max(amend.confirmedAtMillis, deliveryResumedAtMillis);
}

/**
 * Whether an incoming row was generated by the server AFTER it applied the
 * amend. A confirmed amend keeps its old `lastUpdateTs` (the server publishes
 * the new revision), so timestamps alone can't tell a post-amend row from one
 * that predates the confirmation but is newer than the revision the request
 * was based on — a fill that landed while the amend was in flight, for
 * instance. The amended price and order size can: the server never published
 * them before applying the amend.
 */
function incomingReflectsAmend(incoming: UserOrderEntry, amend: LocalAmendMarker): boolean {
    return (
        parseDollarsString(incoming.priceDollars).equals(parseDollarsString(amend.priceDollars)) &&
        incoming.initialCount === amend.initialCount
    );
}

/**
 * `observedAtMillis` is the point on the client's own timeline that makes this
 * data unable to predate the amend — WS receipt for a live row, request issue
 * for a REST response. Never the moment of application: a buffered WS row can
 * be applied long after arriving (throttled timers in a backgrounded tab), and
 * a REST response can land long after the state it captured.
 */
function shouldIgnoreIncomingOrder(
    existing: UserOrderEntry,
    incoming: UserOrderEntry,
    amend: LocalAmendMarker | undefined,
    observedAtMillis: number,
): boolean {
    const existingMillis = DateTime.fromISO(existing.lastUpdateTs).toMillis();
    const incomingMillis = DateTime.fromISO(incoming.lastUpdateTs).toMillis();
    if (!Number.isFinite(existingMillis) || !Number.isFinite(incomingMillis)) return false;

    // REST pages and buffered websocket batches can complete out of order.
    if (incomingMillis < existingMillis) return true;

    if (!amend) return false;
    // Rows at the revision the amend was based on always carry pre-amend data.
    const basedOnMillis = DateTime.fromISO(amend.basedOnLastUpdateTs).toMillis();
    if (Number.isFinite(basedOnMillis) && incomingMillis <= basedOnMillis) return true;
    // Data observed while a revision generated before the confirmation could
    // still be in flight isn't trusted over the confirmation. Data observed
    // past that window is: by then the server is the source of truth again even
    // if its row doesn't look amended. The window runs from the resumption of
    // delivery when there was one, so time the client spent disconnected or
    // frozen — during which a pre-amend frame can sit undelivered — doesn't
    // count toward it.
    if (observedAtMillis - amendProtectionStartMillis(amend) >= AMEND_PROTECTION_WINDOW_MS) return false;
    return !incomingReflectsAmend(incoming, amend);
}

/**
 * A submitted placeholder's browser-generated timestamp cannot be ordered
 * against server timestamps, but an amend confirmed while that placeholder is
 * visible still needs the same value-based protection as an amended canonical
 * row. The first server row wins immediately when there is no amend, or when it
 * reflects the amend; a pre-amend row stays suppressed for the normal bounded
 * delivery window.
 */
function shouldIgnoreIncomingOrderForSubmittedPlaceholder(
    incoming: UserOrderEntry,
    amend: LocalAmendMarker | undefined,
    observedAtMillis: number,
): boolean {
    if (!amend) return false;
    if (observedAtMillis - amendProtectionStartMillis(amend) >= AMEND_PROTECTION_WINDOW_MS) return false;
    return !incomingReflectsAmend(incoming, amend);
}

export const useUserOrdersStore = create<UserOrdersState>()((set, get) => ({
    orders: new Map<string, UserOrderEntry>(),
    locallySubmittedOrderIds: new Set<string>(),
    locallyAmendedOrders: new Map<string, LocalAmendMarker>(),
    loadedAllRestingOrders: false,
    actions: {
        applySubmittedOrder: (vo) => {
            const { locallySubmittedOrderIds, orders: current } = get();
            // Without a ticker the row cannot be canceled through the V2
            // endpoint, so never expose an unusable resting-order action.
            if (!vo.market_ticker) return;
            // The user_orders row can beat the HTTP success callback. It is
            // canonical and more complete than the lightweight V2 result.
            if (current.has(vo.order_id)) return;
            const nextOrders = new Map(current);
            nextOrders.set(vo.order_id, userOrderEntryFromRestVo(vo));
            const nextLocallySubmittedOrderIds = new Set(locallySubmittedOrderIds);
            nextLocallySubmittedOrderIds.add(vo.order_id);
            set({ orders: nextOrders, locallySubmittedOrderIds: nextLocallySubmittedOrderIds });
        },
        hydrateFromRest: (vos, requestedAtMillis) => {
            if (vos.length === 0) return;
            const { locallyAmendedOrders, locallySubmittedOrderIds, orders: current } = get();
            let nextOrders: Map<string, UserOrderEntry> | null = null;
            let nextLocallyAmendedOrders: Map<string, LocalAmendMarker> | null = null;
            let nextLocallySubmittedOrderIds: Set<string> | null = null;
            for (const vo of vos) {
                const existing = (nextOrders ?? current).get(vo.order_id);
                const entry = userOrderEntryFromRestVo(vo, existing);
                const amend = (nextLocallyAmendedOrders ?? locallyAmendedOrders).get(entry.id);
                const isSubmittedPlaceholder = (nextLocallySubmittedOrderIds ?? locallySubmittedOrderIds).has(entry.id);
                if (
                    existing &&
                    (isSubmittedPlaceholder
                        ? shouldIgnoreIncomingOrderForSubmittedPlaceholder(entry, amend, requestedAtMillis)
                        : shouldIgnoreIncomingOrder(existing, entry, amend, requestedAtMillis))
                )
                    continue;
                if (existing && userOrderEntriesEqual(existing, entry)) {
                    if (isSubmittedPlaceholder) {
                        nextLocallySubmittedOrderIds =
                            nextLocallySubmittedOrderIds ?? new Set(locallySubmittedOrderIds);
                        nextLocallySubmittedOrderIds.delete(entry.id);
                    }
                    continue;
                }
                nextOrders = nextOrders ?? new Map(current);
                nextOrders.set(entry.id, entry);
                if (amend) {
                    nextLocallyAmendedOrders = nextLocallyAmendedOrders ?? new Map(locallyAmendedOrders);
                    nextLocallyAmendedOrders.delete(entry.id);
                }
                if (isSubmittedPlaceholder) {
                    nextLocallySubmittedOrderIds = nextLocallySubmittedOrderIds ?? new Set(locallySubmittedOrderIds);
                    nextLocallySubmittedOrderIds.delete(entry.id);
                }
            }
            if (nextOrders || nextLocallyAmendedOrders || nextLocallySubmittedOrderIds) {
                set({
                    orders: nextOrders ?? current,
                    locallyAmendedOrders: nextLocallyAmendedOrders ?? locallyAmendedOrders,
                    locallySubmittedOrderIds: nextLocallySubmittedOrderIds ?? locallySubmittedOrderIds,
                });
            }
        },
        applyWsOrders: (rows) => {
            if (rows.length === 0) return;
            const { locallyAmendedOrders, locallySubmittedOrderIds, orders: current } = get();
            let nextOrders: Map<string, UserOrderEntry> | null = null;
            let nextLocallyAmendedOrders: Map<string, LocalAmendMarker> | null = null;
            let nextLocallySubmittedOrderIds: Set<string> | null = null;
            for (const { dto, receivedAtMillis } of rows) {
                const existing = (nextOrders ?? current).get(dto.order_id);
                const isSubmittedPlaceholder = (nextLocallySubmittedOrderIds ?? locallySubmittedOrderIds).has(
                    dto.order_id,
                );
                const wsEntry = userOrderEntryFromWsDto(dto, existing);
                // Normal live updates preserve the original create timestamp.
                // A V2 placeholder's timestamp is browser-generated, though,
                // so its first server row must replace that field too.
                const entry = isSubmittedPlaceholder ? { ...wsEntry, createTs: dto.create_ts } : wsEntry;
                const amend = (nextLocallyAmendedOrders ?? locallyAmendedOrders).get(entry.id);
                if (
                    existing &&
                    (isSubmittedPlaceholder
                        ? shouldIgnoreIncomingOrderForSubmittedPlaceholder(entry, amend, receivedAtMillis)
                        : shouldIgnoreIncomingOrder(existing, entry, amend, receivedAtMillis))
                )
                    continue;
                if (existing && userOrderEntriesEqual(existing, entry)) {
                    if (isSubmittedPlaceholder) {
                        nextLocallySubmittedOrderIds =
                            nextLocallySubmittedOrderIds ?? new Set(locallySubmittedOrderIds);
                        nextLocallySubmittedOrderIds.delete(entry.id);
                    }
                    continue;
                }
                nextOrders = nextOrders ?? new Map(current);
                nextOrders.set(entry.id, entry);
                if (amend) {
                    nextLocallyAmendedOrders = nextLocallyAmendedOrders ?? new Map(locallyAmendedOrders);
                    nextLocallyAmendedOrders.delete(entry.id);
                }
                if (isSubmittedPlaceholder) {
                    nextLocallySubmittedOrderIds = nextLocallySubmittedOrderIds ?? new Set(locallySubmittedOrderIds);
                    nextLocallySubmittedOrderIds.delete(entry.id);
                }
            }
            if (nextOrders || nextLocallyAmendedOrders || nextLocallySubmittedOrderIds) {
                set({
                    orders: nextOrders ?? current,
                    locallyAmendedOrders: nextLocallyAmendedOrders ?? locallyAmendedOrders,
                    locallySubmittedOrderIds: nextLocallySubmittedOrderIds ?? locallySubmittedOrderIds,
                });
            }
        },
        removeOrder: (orderId) => {
            get().actions.removeOrders([orderId]);
        },
        removeOrders: (orderIds) => {
            if (orderIds.length === 0) return;
            const { locallyAmendedOrders, locallySubmittedOrderIds, orders: current } = get();
            let nextOrders: Map<string, UserOrderEntry> | null = null;
            let nextLocallyAmendedOrders: Map<string, LocalAmendMarker> | null = null;
            let nextLocallySubmittedOrderIds: Set<string> | null = null;
            for (const orderId of orderIds) {
                if ((nextOrders ?? current).has(orderId)) {
                    nextOrders = nextOrders ?? new Map(current);
                    nextOrders.delete(orderId);
                }
                if ((nextLocallyAmendedOrders ?? locallyAmendedOrders).has(orderId)) {
                    nextLocallyAmendedOrders = nextLocallyAmendedOrders ?? new Map(locallyAmendedOrders);
                    nextLocallyAmendedOrders.delete(orderId);
                }
                if ((nextLocallySubmittedOrderIds ?? locallySubmittedOrderIds).has(orderId)) {
                    nextLocallySubmittedOrderIds = nextLocallySubmittedOrderIds ?? new Set(locallySubmittedOrderIds);
                    nextLocallySubmittedOrderIds.delete(orderId);
                }
            }
            if (nextOrders || nextLocallyAmendedOrders || nextLocallySubmittedOrderIds) {
                set({
                    orders: nextOrders ?? current,
                    locallyAmendedOrders: nextLocallyAmendedOrders ?? locallyAmendedOrders,
                    locallySubmittedOrderIds: nextLocallySubmittedOrderIds ?? locallySubmittedOrderIds,
                });
            }
        },
        applyAmendResult: (orderId, expectedLastUpdateTs, result) => {
            const current = get().orders;
            const existing = current.get(orderId);
            if (!existing) return;
            const amend: LocalAmendMarker = {
                basedOnLastUpdateTs: expectedLastUpdateTs,
                confirmedAtMillis: Date.now(),
                priceDollars: result.priceDollars,
                initialCount: result.initialCount,
            };
            // A revision that landed after the mutation started is only newer
            // than the lightweight write response if the server generated it
            // AFTER applying the amend — then it is the more complete state and
            // the confirmation adds nothing. A revision that does NOT reflect
            // the amend was generated before it (the server never published the
            // amended price/size earlier), so it is pre-amend however new its
            // timestamp looks — a fill that landed while the request was in
            // flight and drained before the success callback, typically. The
            // confirmation is generated at amend time, i.e. after that fill, so
            // it is the later state and must still be applied; dropping it left
            // the row showing pre-amend values under a success snackbar, with
            // no marker to keep later pre-amend rows out.
            if (existing.lastUpdateTs !== expectedLastUpdateTs && incomingReflectsAmend(existing, amend)) return;
            const next = new Map(current);
            next.set(orderId, {
                ...existing,
                ...result,
            });
            // Resolve what the WS pump is already holding for this order here,
            // rather than trusting the drain to happen while the protection
            // window is still open — that is what makes the outcome
            // independent of when a throttled `setTimeout` fires.
            discardPreAmendBufferedWsUserOrder(orderId, existing, amend);
            const locallyAmendedOrders = new Map(get().locallyAmendedOrders);
            locallyAmendedOrders.set(orderId, amend);
            set({ orders: next, locallyAmendedOrders });
        },
        dropRestingOrdersMissingFromRefetch: (snapshot, fetchedOrderIds, snapshotAtMillis) => {
            const { locallyAmendedOrders, orders: current } = get();
            let nextOrders: Map<string, UserOrderEntry> | null = null;
            let nextLocallyAmendedOrders: Map<string, LocalAmendMarker> | null = null;
            snapshot.forEach((lastUpdateTsAtSnapshot, orderId) => {
                if (fetchedOrderIds.has(orderId)) return;
                const order = (nextOrders ?? current).get(orderId);
                if (!order || order.remainingCount <= 0 || isUserOrderPending(order)) return;
                const amend = locallyAmendedOrders.get(orderId);
                if (amend && snapshotAtMillis < amendProtectionStartMillis(amend) + AMEND_PROTECTION_WINDOW_MS) return;
                if (order.lastUpdateTs !== lastUpdateTsAtSnapshot) return;
                nextOrders = nextOrders ?? new Map(current);
                nextOrders.delete(orderId);
                if (amend) {
                    nextLocallyAmendedOrders = nextLocallyAmendedOrders ?? new Map(locallyAmendedOrders);
                    nextLocallyAmendedOrders.delete(orderId);
                }
            });
            if (nextOrders || nextLocallyAmendedOrders) {
                set({
                    orders: nextOrders ?? current,
                    locallyAmendedOrders: nextLocallyAmendedOrders ?? locallyAmendedOrders,
                });
            }
        },
        allRestingOrdersLoaded: () => set({ loadedAllRestingOrders: true }),
        reset: () =>
            set({
                orders: new Map<string, UserOrderEntry>(),
                locallySubmittedOrderIds: new Set<string>(),
                locallyAmendedOrders: new Map<string, LocalAmendMarker>(),
                loadedAllRestingOrders: false,
            }),
    },
}));

/**
 * True when this event already has at least one order cached, regardless of
 * how it got here — a market-scoped load (e.g. the resting-orders modal)
 * writes into this same shared cache without going through
 * `refreshOrdersForEvent`, so a caller deciding whether an event's FIRST
 * fetch this session can skip reconciliation must check this, not just its
 * own fetch-tracking state.
 *
 * `resolveEventTicker` re-resolves each order's event at check time rather
 * than trusting anything stored on the entry — market data can be registered
 * (or scoped stores swapped) after the order was cached, so only a fresh
 * lookup is trustworthy. Callers pass the same market-store-backed resolver
 * (`resolveEventTickerForMarketId`) used elsewhere for order/event resolution.
 */
export function hasCachedOrdersForEvent(
    orders: ReadonlyMap<string, UserOrderEntry>,
    eventTicker: string,
    resolveEventTicker: (marketId: string) => string,
): boolean {
    for (const order of orders.values()) {
        if (resolveEventTicker(order.marketId) === eventTicker) return true;
    }
    return false;
}

/**
 * Snapshot (order id → `lastUpdateTs`) of the event's cached orders that
 * currently look resting, taken by `refreshOrdersForEvent` below right
 * before it issues a reconciliation refetch. Pending orders (optimistic rows
 * whose status is still settling) have `remainingCount > 0` but aren't in the
 * backend's resting set yet, so a resting-only refetch won't return them —
 * excluding them here keeps `dropRestingOrdersMissingFromRefetch` from
 * deleting a live pending order.
 */
export function snapshotRestingOrderUpdateTimesForEvent(
    orders: ReadonlyMap<string, UserOrderEntry>,
    eventTicker: string,
    resolveEventTicker: (marketId: string) => string,
): Map<string, string> {
    const snapshot = new Map<string, string>();
    orders.forEach((order, orderId) => {
        if (order.remainingCount <= 0 || isUserOrderPending(order)) return;
        if (resolveEventTicker(order.marketId) !== eventTicker) return;
        snapshot.set(orderId, order.lastUpdateTs);
    });
    return snapshot;
}

// ---------------------------------------------------------------------------
// WS order pump + refreshOrdersForEvent (formerly UserStore's WS message
// buffering and event-scoped resting-orders refetch). Plain module-scope
// state, not React state — nothing renders off it directly; consumers read
// the effects of it through `useUserOrdersStore` above.
// ---------------------------------------------------------------------------

const ORDERS_REFRESH_ERROR_LOG_DEDUPE_MS = 5 * 60 * 1000;
const ORDERS_REFRESH_ERROR_LOG_MAX_CONTEXTS = 200;

let wsUserOrderMessagesBuffer: Record<string, ReceivedWsUserOrder> = {};
let lastUserOrderWsProcessedTs: DateTime | undefined = undefined;
let wsUserOrderFlushTimeout: ReturnType<typeof setTimeout> | null = null;
let unsubscribeOrdersWs: WsUnsubscribeCallback | null = null;
let unsubscribeOrdersWsConnectionState: (() => void) | null = null;
let visibilityResumeHandler: (() => void) | null = null;
const inFlightOrdersContexts = new Map<string, number>();
const lastOrdersRefreshErrorLogs = new Map<string, { errorKey: string; loggedAt: number }>();

function getOrdersContextKeyForEvent(eventTicker: string): string {
    return `orders:event:${eventTicker}`;
}

function startOrdersRefresh(contextKey: string): void {
    const current = inFlightOrdersContexts.get(contextKey) ?? 0;
    inFlightOrdersContexts.set(contextKey, current + 1);
}

function finishOrdersRefresh(contextKey: string): void {
    const current = inFlightOrdersContexts.get(contextKey);
    if (!current || current <= 1) {
        inFlightOrdersContexts.delete(contextKey);
        return;
    }
    inFlightOrdersContexts.set(contextKey, current - 1);
}

// Note: the WS drain path (below) calls this with a market id, not an event
// ticker, same as the original UserStore implementation — `contextKey` ends
// up namespaced `orders:event:${marketId}` there, distinct from the
// `refreshOrdersForEvent`-populated keys. Preserved verbatim; not fixed here.
function isOrdersContextInFlight(eventTicker: string): boolean {
    return inFlightOrdersContexts.has(getOrdersContextKeyForEvent(eventTicker));
}

function shouldSuppressOrdersRefreshError(error: ServerError): boolean {
    return (
        error.cancelled ||
        error.notFound ||
        error.timeout ||
        error.tooManyRequests ||
        error.unauthorized ||
        error.forbidden
    );
}

function getOrdersRefreshErrorKey(error: ServerError): string {
    const category =
        [
            error.unknown && 'unknown',
            error.unavailable && 'unavailable',
            error.internal && 'internal',
            error.invalid && 'invalid',
            error.conflict && 'conflict',
            error.maintenance && 'maintenance',
        ]
            .filter(Boolean)
            .join(',') || 'unclassified';
    const code = error.error?.code || 'no_code';
    const message = error.error?.message || error.error?.details || 'no_message';
    return `category=${category}; code=${code}; message=${message}`;
}

function markOrdersRefreshFailed(contextKey: string, error: ServerError): void {
    const errorKey = getOrdersRefreshErrorKey(error);
    if (shouldSuppressOrdersRefreshError(error)) {
        return;
    }

    const now = Date.now();
    const previous = lastOrdersRefreshErrorLogs.get(contextKey);
    if (previous?.errorKey === errorKey && now - previous.loggedAt < ORDERS_REFRESH_ERROR_LOG_DEDUPE_MS) {
        return;
    }

    if (lastOrdersRefreshErrorLogs.size >= ORDERS_REFRESH_ERROR_LOG_MAX_CONTEXTS) {
        const oldest = lastOrdersRefreshErrorLogs.keys().next().value;
        if (oldest !== undefined) {
            lastOrdersRefreshErrorLogs.delete(oldest);
        }
    }
    lastOrdersRefreshErrorLogs.set(contextKey, { errorKey, loggedAt: now });
    logProviderFactory.logError(`Error fetching orders: ${errorKey}`);
}

// Fetches one page of the event's CURRENT resting orders and writes it into
// the shared orders cache. `onSuc`'s `fetchedOrderIds` reports every order id
// the page returned even when a stale response skips the cache write — the
// caller's reconciliation (`dropRestingOrdersMissingFromRefetch`) applies its
// own staleness guard at completion time. `isStale` is re-checked immediately
// before writing the response into the shared orders cache (not just at the
// caller's completion handler) — a logout/subaccount-switch/stale-reconnect
// can invalidate this request while it's in flight, and applying its response
// after that point would repopulate the cache with data that no longer
// belongs to the current session/subaccount.
function loadRestingOrdersPageForEvent(
    eventTicker: string,
    pageSize: number,
    cursor: string | undefined,
    onSuc: (fetchedOrderIds: Array<string>, couldHaveMore: boolean, cursor?: string) => void,
    onErr: (err: ServerError) => void,
    onLog?: (parsedError: ServerError) => void,
    isStale?: () => boolean,
): void {
    const { userId, isLoggedInMember } = useAuthStore.getState();
    if (!eventTicker || !(userId ?? '') || !isLoggedInMember) {
        return;
    }
    const requestedAtMillis = Date.now();
    getOrders(
        userId ?? '',
        (data, newCursor) => {
            if (!isStale?.()) {
                useUserOrdersStore.getState().actions.hydrateFromRest(data, requestedAtMillis);
                useBannerStore.getState().actions.queryExchangeRecovered();
            }
            onSuc(
                data.map((vo) => vo.order_id),
                !!newCursor,
                newCursor,
            );
        },
        onErr,
        noop,
        {
            event_ticker: eventTicker,
            page_size: pageSize,
            cursor: cursor,
            status: 'resting',
        },
        onLog,
    );
}

/**
 * Refetches an event's resting orders and writes them into the shared cache.
 * `onSettled` lets callers (namely `tradingSync`'s "fetch resting orders once
 * per session" tracking) know whether the fetch actually completed, so a
 * failed attempt — or one skipped because another was already in flight —
 * doesn't get treated as done and permanently skipped thereafter.
 *
 * `reconcile` corrects locally-cached orders that may have left the resting
 * set (filled/canceled) while unwatched — during a WS gap, or while the
 * event's context was inactive. A resting-only response can only ever
 * ADD/update rows, so the refetch also snapshots the event's cached resting
 * orders up front and, once the last page lands, drops any cached row the
 * authoritative resting response no longer returned. This keeps every
 * request scoped to `status=resting` — re-downloading the event's entire
 * order history (executed/canceled included) just to detect removals would
 * make query-exchange scan far more rows than the handful of orders a single
 * user has resting on one event. A true first-ever fetch for an event has no
 * cached rows to reconcile, so it skips the snapshot entirely.
 *
 * `resolveEventTicker` resolves an order's parent event via the caller's
 * market-store lookup — the same one `tradingSync.ts` already threads through
 * `hasCachedOrdersForEvent` (`resolveEventTickerForMarketId` from
 * `marketEntitiesStore`, passed in by the caller rather than imported directly here).
 */
export function refreshOrdersForEvent(
    eventTicker: string,
    resolveEventTicker: (marketId: string) => string,
    options: {
        reconcile?: boolean;
        onSettled?: (succeeded: boolean) => void;
        isStale?: () => boolean;
    } = {},
): void {
    const onSettled = options.onSettled ?? noop;
    const isLoggedInMember = useAuthStore.getState().isLoggedInMember;
    if (!isLoggedInMember || !eventTicker) {
        onSettled(false);
        return;
    }
    const contextKey = getOrdersContextKeyForEvent(eventTicker);
    if (inFlightOrdersContexts.has(contextKey)) {
        onSettled(false);
        return;
    }
    startOrdersRefresh(contextKey);

    // A single user's resting orders on one event are almost always a
    // handful of rows, and the backend runs a `LIMIT page_size + 1` scan per
    // page — so a huge page size is pure cost. The cursor loop below already
    // follows pages for the rare user with more.
    const PAGE_SIZE = 200;
    const restingSnapshot = options.reconcile
        ? snapshotRestingOrderUpdateTimesForEvent(useUserOrdersStore.getState().orders, eventTicker, resolveEventTicker)
        : null;
    // Taken with the snapshot: `dropRestingOrdersMissingFromRefetch` compares
    // it against each locally confirmed amend to tell a reconciliation that
    // predates the amend (not authoritative about it) from a later one.
    const snapshotAtMillis = Date.now();
    const fetchedOrderIds = new Set<string>();

    const onRefreshError = (): void => {
        finishOrdersRefresh(contextKey);
        onSettled(false);
    };

    const loadPage = (cursor?: string): void => {
        loadRestingOrdersPageForEvent(
            eventTicker,
            PAGE_SIZE,
            cursor,
            (pageOrderIds, couldHaveMore, nextCursor) => {
                pageOrderIds.forEach((orderId) => fetchedOrderIds.add(orderId));
                if (couldHaveMore && nextCursor !== cursor) {
                    loadPage(nextCursor);
                } else {
                    // Only drop stale rows off a cleanly-finished pagination
                    // (`!couldHaveMore`) — bailing on a stuck cursor means
                    // later pages were never seen, and a fetch flagged stale
                    // mid-flight must not touch the cache at all (same guard
                    // `loadRestingOrdersPageForEvent` applies to its own
                    // writes).
                    if (!couldHaveMore && restingSnapshot?.size && !options.isStale?.()) {
                        useUserOrdersStore
                            .getState()
                            .actions.dropRestingOrdersMissingFromRefetch(
                                restingSnapshot,
                                fetchedOrderIds,
                                snapshotAtMillis,
                            );
                    }
                    finishOrdersRefresh(contextKey);
                    onSettled(true);
                }
            },
            onRefreshError,
            (error) => markOrdersRefreshFailed(contextKey, error),
            options.isStale,
        );
    };

    loadPage();
}

function queueWsUserOrderMessages(response: WsResponse): void {
    const messages = typeof response === 'object' ? [response as WsMessage] : (response as WsMessage[]);

    const activeSubaccount = getSubaccountNumber();
    for (const msg of messages) {
        if (msg.type === 'user_order') {
            const payload = msg.msg as UserOrderDTO;
            if ((payload.subaccount ?? 0) !== activeSubaccount) continue;
            // Stamped on arrival: the drain that applies this row can run much
            // later than now, and staleness checks must not read that clock.
            wsUserOrderMessagesBuffer[payload.order_id] = { dto: payload, receivedAtMillis: Date.now() };
        }
    }

    // Process messages buffer if it's been a while since last processing
    const neverProcessed = !lastUserOrderWsProcessedTs;
    const processedTooLongAgo =
        !!lastUserOrderWsProcessedTs &&
        DateTime.now().diff(lastUserOrderWsProcessedTs).as('milliseconds') > WS_USER_ORDER_MAX_BUFFER_MS;
    const smallBatch =
        Object.keys(wsUserOrderMessagesBuffer).length <= 2 &&
        !!lastUserOrderWsProcessedTs &&
        DateTime.now().diff(lastUserOrderWsProcessedTs).as('milliseconds') > 1000;
    if (neverProcessed || processedTooLongAgo || smallBatch) {
        processWsUserOrderMessagesBuffer();
        return;
    }
    // Every condition above is evaluated on message ARRIVAL, so a batch left
    // buffered here would otherwise wait for the next message — indefinitely
    // on an idle channel. Schedule the drain instead, so a queued row is
    // always applied within `WS_USER_ORDER_MAX_BUFFER_MS` of arriving. Other
    // logic depends on that bound (see `AMEND_PROTECTION_WINDOW_MS`), and it
    // also keeps order updates from stalling out of the UI entirely.
    scheduleWsUserOrderMessagesFlush();
}

/**
 * Settles the row the pump is buffering for a just-amended order, so a delayed
 * drain can't decide the outcome later.
 *
 * Arriving before the amend's success callback doesn't make a row pre-amend:
 * the server can apply the amend and publish its revision (or a fill on top of
 * it) before the HTTP response is handled here. Such a row is the only complete
 * newer state there is — the write response carries just price and counts — so
 * it stays buffered and is applied on the next drain. A row that does NOT
 * reflect the amend can only predate it, and is dropped: nothing is lost with
 * it, since every row carries the order's full state.
 */
function discardPreAmendBufferedWsUserOrder(orderId: string, existing: UserOrderEntry, amend: LocalAmendMarker): void {
    const buffered = wsUserOrderMessagesBuffer[orderId];
    if (!buffered) return;
    if (incomingReflectsAmend(userOrderEntryFromWsDto(buffered.dto, existing), amend)) return;
    delete wsUserOrderMessagesBuffer[orderId];
}

function scheduleWsUserOrderMessagesFlush(): void {
    if (wsUserOrderFlushTimeout !== null) return;
    // A macrotask, deliberately: a microtask flush lands inside the same task
    // as the WS callbacks, and each store write re-renders subscribers
    // synchronously.
    wsUserOrderFlushTimeout = setTimeout(processWsUserOrderMessagesBuffer, WS_USER_ORDER_MAX_BUFFER_MS);
}

function clearWsUserOrderMessagesFlush(): void {
    if (wsUserOrderFlushTimeout === null) return;
    clearTimeout(wsUserOrderFlushTimeout);
    wsUserOrderFlushTimeout = null;
}

function processWsUserOrderMessagesBuffer(): void {
    clearWsUserOrderMessagesFlush();

    // If buffer is empty, nothing to process
    if (Object.keys(wsUserOrderMessagesBuffer).length === 0) {
        return;
    }

    // Get all buffered messages
    const rows = Object.values(wsUserOrderMessagesBuffer);

    // Clear the buffer
    wsUserOrderMessagesBuffer = {};

    // Re-check the active subaccount at drain time. The enqueue path
    // (`queueWsUserOrderMessages`) already drops mismatched messages, but a
    // subaccount switch can happen *between* enqueue and drain — the queued
    // flush runs on a 1s/4s cadence — so anything buffered for the
    // previously-active subaccount must be discarded now to avoid
    // resurrecting stale orders post-switch.
    const activeSubaccount = getSubaccountNumber();
    const inScopeRows = rows.filter((row) => (row.dto.subaccount ?? 0) === activeSubaccount);

    // Eliminate duplicates by order ID, keeping only the latest message per order
    const uniqueRowsMap: Record<string, ReceivedWsUserOrder> = {};
    inScopeRows.forEach((row) => {
        uniqueRowsMap[row.dto.order_id] = row;
    });
    const uniqueRows = Object.values(uniqueRowsMap);

    // Process each unique message, batching the survivors into a single
    // store write so one drain triggers at most one subscriber pass.
    const rowsToApply: ReceivedWsUserOrder[] = [];
    uniqueRows.forEach((row) => {
        const eventTicker = resolveEventTickerForMarketId(row.dto.market_id) || undefined;

        const tradingContextState = useTradingContextStore.getState();
        const hasPortfolioContext = tradingContextSelectors.portfolioActive(tradingContextState);
        const hasOrderContext =
            !!eventTicker && tradingContextSelectors.hasOrderEventTicker(tradingContextState, eventTicker);

        let hasEventContext = false;
        if (!hasPortfolioContext && !hasOrderContext) {
            hasEventContext = !!eventTicker && tradingContextSelectors.hasEventTicker(tradingContextState, eventTicker);
        }

        const shouldProcess = hasPortfolioContext || hasOrderContext || hasEventContext;
        if (shouldProcess && !isOrdersContextInFlight(row.dto.market_id)) {
            rowsToApply.push(row);
        }
    });
    useUserOrdersStore.getState().actions.applyWsOrders(rowsToApply);

    // Update last processed timestamp
    lastUserOrderWsProcessedTs = DateTime.now();
}

/** Subscribes to the `user_orders` WS channel; called once at session bootstrap. */
export function subscribeUserOrdersWs(): void {
    unsubscribeOrdersWs = legacyExchangeWsClient.subscribe({ channels: ['user_orders'] }, queueWsUserOrderMessages);
    // Both feed `deliveryResumedAtMillis`: a socket that just came back and a
    // tab that just came back are the two ways a row the server generated long
    // ago reaches `queueWsUserOrderMessages` only now. Detach first — this runs
    // on every session start, and a second attach would otherwise leak the
    // previous pair.
    detachDeliveryResumeListeners();
    unsubscribeOrdersWsConnectionState = legacyExchangeWsClient.subscribeConnectionState((state) => {
        if (state.connected) deliveryResumedAtMillis = Date.now();
    });
    if (typeof document !== 'undefined') {
        visibilityResumeHandler = () => {
            if (document.visibilityState === 'visible') deliveryResumedAtMillis = Date.now();
        };
        document.addEventListener('visibilitychange', visibilityResumeHandler);
    }
}

function detachDeliveryResumeListeners(): void {
    unsubscribeOrdersWsConnectionState && unsubscribeOrdersWsConnectionState();
    unsubscribeOrdersWsConnectionState = null;
    if (visibilityResumeHandler) {
        document.removeEventListener('visibilitychange', visibilityResumeHandler);
        visibilityResumeHandler = null;
    }
}

/** Unsubscribes from the `user_orders` WS channel; called from session teardown. */
export function unsubscribeUserOrdersWs(): void {
    unsubscribeOrdersWs && unsubscribeOrdersWs();
    detachDeliveryResumeListeners();
    // Anything still buffered belongs to the session being torn down: drop the
    // scheduled drain so it can't write into the store afterwards. The buffer
    // itself is left as it was (see `resetUserOrdersWsInternalsForTesting`).
    clearWsUserOrderMessagesFlush();
}

/** Clears in-flight/error-log bookkeeping on logout/forced-disconnect teardown. */
export function resetUserOrdersRefreshState(): void {
    inFlightOrdersContexts.clear();
    lastOrdersRefreshErrorLogs.clear();
}

/**
 * Test-only: resets the WS buffer/flush-timing state this module keeps for
 * the lifetime of the page session (production never resets it on logout
 * either — see `resetUserOrdersRefreshState`), so each test starts from a
 * clean "never processed" state instead of inheriting timing from whatever
 * test ran previously in the same file.
 */
export function resetUserOrdersWsInternalsForTesting(): void {
    clearWsUserOrderMessagesFlush();
    wsUserOrderMessagesBuffer = {};
    lastUserOrderWsProcessedTs = undefined;
    deliveryResumedAtMillis = 0;
}

/** Resting orders in one market, newest first. */
export function useRestingOrdersInMarket(marketId: string): UserOrderEntry[] {
    const orders = useUserOrdersStore((s) => s.orders);
    return selectRestingUserOrdersInMarket(orders, marketId);
}

/**
 * Number of resting orders in one market. Primitive selector, so subscribers
 * (e.g. every market tile on an event page) only re-render when their
 * market's count actually changes.
 */
export function useNumRestingOrdersInMarket(marketId: string): number {
    return useUserOrdersStore((s) => {
        let count = 0;
        s.orders.forEach((order) => {
            if (order.remainingCount > 0 && order.marketId === marketId) count += 1;
        });
        return count;
    });
}

let authUnsubscribe: (() => void) | null = null;
let subaccountUnsubscribe: (() => void) | null = null;

function ensureResetSubscriptions(): void {
    if (!authUnsubscribe) {
        // The forced-disconnect teardown path also flips `isLoggedInMember`
        // (TransportLayer sets the zustand logout state), so this covers both
        // explicit logout and session-expiry — matching the old
        // `OrderStore.dispose()` wipe via the app logout teardown.
        authUnsubscribe = useAuthStore.subscribe((state, prev) => {
            if (state.isLoggedInMember === prev.isLoggedInMember || state.isLoggedInMember) return;
            useUserOrdersStore.getState().actions.reset();
        });
    }
    if (!subaccountUnsubscribe) {
        // Drop in-memory orders the moment the active subaccount changes —
        // they belong to the previous subaccount.
        subaccountUnsubscribe = useSubAccountStore.subscribe((state, prev) => {
            if (state.subaccountNumber === prev.subaccountNumber) return;
            useUserOrdersStore.getState().actions.reset();
        });
    }
}

if (typeof window !== 'undefined') {
    ensureResetSubscriptions();
}
